import { Icon, LiveSearchableDropdown, type LiveSearchItem } from '@civilgrid/ui/components';
import { useDebouncedValue } from '@civilgrid/ui/hooks';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { fetchProjects } from '~/api/projectsV3';
import { ORG_SWITCHER_CURRENT_PROJECT_KEY, ORG_SWITCHER_PROJECTS_KEY } from '~/common/queryKeys';
import { CIVIL_DRAW_ROUTE } from '~/common/types';
import type { ProjectDetailsParams } from '~/pages/projectDetails';
import rootStore from '../../../../store';
import { DEBOUNCE_MS, PAGE_SIZE, STALE_TIME } from './constants';

function useProjectPrefetch(organizationId: string) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!organizationId) return;
    void queryClient.prefetchInfiniteQuery({
      queryKey: [...ORG_SWITCHER_PROJECTS_KEY, organizationId, ''],
      queryFn: () => fetchProjects(organizationId, { globalFilter: '', limit: PAGE_SIZE, skip: 0 }),
      initialPageParam: 0,
      staleTime: STALE_TIME,
    });
  }, [queryClient, organizationId]);
}

function useProjectSearch(organizationId: string) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, DEBOUNCE_MS);

  const query = useInfiniteQuery({
    // Must keep the shared prefix: saving project details invalidates it to refresh the trigger label.
    queryKey: [...ORG_SWITCHER_PROJECTS_KEY, organizationId, debouncedSearch],
    queryFn: ({ pageParam }) =>
      fetchProjects(organizationId, {
        limit: PAGE_SIZE,
        skip: (pageParam as number) * PAGE_SIZE,
        ...(debouncedSearch && {
          filters: [
            {
              filterType: 'and',
              filters: [{ property: 'name', operator: 'includes', value: debouncedSearch }],
            },
          ],
        }),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.length * PAGE_SIZE;
      return loaded < lastPage.count ? allPages.length : undefined;
    },
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
  });

  const items = useMemo<LiveSearchItem[]>(
    () => query.data?.pages.flatMap((page) => page.projects.map((p) => ({ value: p.id, label: p.name }))) ?? [],
    [query.data],
  );

  const hasMore = query.hasNextPage ?? false;

  return {
    items,
    hasMore,
    isLoading: query.isFetching && items.length === 0,
    isLoadingMore: query.isFetchingNextPage,
    search,
    setSearch,
    fetchNextPage: query.fetchNextPage,
  };
}

/**
 * Fetches the name of a single project by ID so the trigger always shows
 * the current project's name, even if it falls outside the paginated list.
 */
function useCurrentProjectName(organizationId: string, projectId: string | undefined) {
  const { data } = useQuery({
    // Must keep the shared prefix: saving project details invalidates it to refresh this name.
    queryKey: [...ORG_SWITCHER_CURRENT_PROJECT_KEY, organizationId, projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const result = await fetchProjects(organizationId, {
        limit: 1,
        skip: 0,
        filters: [
          {
            filterType: 'and',
            filters: [{ property: 'id', operator: 'eq', value: projectId }],
          },
        ],
      });
      return result.projects[0]?.name ?? null;
    },
    enabled: !!organizationId && !!projectId,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  });

  return data ?? '';
}

export function ProjectSwitcher() {
  const navigate = useNavigate();
  const { projectId, tabId, mapId } = useParams({ strict: false }) as ProjectDetailsParams;

  const { currentOrganization } = useSnapshot(rootStore) as typeof rootStore;
  const organizationId = currentOrganization?.id ?? '';

  useProjectPrefetch(organizationId);
  const project = useProjectSearch(organizationId);
  const currentProjectName = useCurrentProjectName(organizationId, projectId);

  const handleSearchChange = useCallback((search: string) => project.setSearch(search), [project.setSearch]);

  const handleLoadMore = useCallback(() => void project.fetchNextPage(), [project.fetchNextPage]);

  return (
    <LiveSearchableDropdown
      items={project.items}
      hasMore={project.hasMore}
      isLoading={project.isLoading}
      isLoadingMore={project.isLoadingMore}
      onSearchChange={handleSearchChange}
      onLoadMore={handleLoadMore}
      value={projectId}
      onSelect={(item) => {
        if (tabId && tabId !== CIVIL_DRAW_ROUTE) {
          if (mapId) {
            navigate({
              to: '/projects/$projectId/$tabId/$mapId',
              params: { projectId: item.value, tabId, mapId },
            });
          } else {
            navigate({
              to: '/projects/$projectId/$tabId',
              params: { projectId: item.value, tabId },
            });
          }
        } else {
          navigate({ to: '/projects/$projectId', params: { projectId: item.value } });
        }
      }}
      searchPlaceholder="Search projects..."
      renderTrigger={({ selectedItem }) => (
        <button
          type="button"
          data-testid="top-nav-project-switcher-trigger"
          className="flex h-full text-sm font-noto font-medium text-grey-90 cursor-pointer items-center space-x-2 truncate rounded-[6px] focus-ring"
        >
          <div className="max-w-[12rem] truncate">{selectedItem?.label ?? currentProjectName}</div>
          <Icon icon="chevronDown" />
        </button>
      )}
    />
  );
}
